import React, { useState } from 'react';
import { Container, Card, Form, Row, Col, Button } from 'react-bootstrap';
import { Select } from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import FormItem from '@/common/FormItem/FormItem';
import PageTitle from '@/common/PageTitle/PageTitle';
import { channels } from '@/data/channels';
import { getChannelArtwork } from '@/utils/artwork';
import styles from './music-vendor-roles.module.scss';

const Test: React.FC = () => {
  const navigate = useNavigate();
  
  // Filter state
  const [filters, setFilters] = useState({
    channelName: undefined as string | undefined,
    status: undefined as string | undefined,
    type: undefined as string | undefined,
  });
  // Reset counter for forcing re-render of components
  const [resetCounter, setResetCounter] = useState(0);

  // Generate filter options dynamically
  const filterOptions = {
    channelNames: ['KK Original Coffee Shop', 'KK Original Dance', 'KK Original Pop', 'Pop Hits', 'Country Hits'],
    status: ['Live', 'Approved', 'Not Live'],
    types: ['Original', 'Commercial']
  };

  // Filter change handler
  const handleFilterChange = (filterName: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  // Reset all filters
  const handleReset = () => {
    setFilters({
      channelName: undefined,
      status: undefined,
      type: undefined,
    });
    setResetCounter(prev => prev + 1);
  };

  // Filter channels based on current filters
  const filteredChannels = channels.filter(channel => {
    const matchesChannelName = !filters.channelName || 
      channel.name.toLowerCase().includes(filters.channelName.toLowerCase());
    
    const matchesStatus = !filters.status || 
      channel.status.toLowerCase() === filters.status.toLowerCase();
    
    const matchesType = !filters.type || 
      (filters.type === 'Original' && channel.name.includes('KK Original')) ||
      (filters.type === 'Commercial' && !channel.name.includes('KK Original'));
    
    return matchesChannelName && matchesStatus && matchesType;
  });

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <PageTitle title="Channel Programming" />
              <p style={{ 
                color: '#7f8c8d', 
                fontSize: '14px', 
                margin: '4px 0 0 0',
                fontWeight: '400'
              }}>
                Choose a channel to program music and manage rotations
              </p>
            </div>
            <Button 
              onClick={handleReset}
              style={{ 
                background: 'white', 
                color: '#333', 
                border: '1px solid #e0e0e0', 
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          <Form className={styles.form}>
            <Row>
              <Col>
                <Row className="mb-2">
                  <Col>
                    <Row>
                      <Col md={4}>
                        <FormItem
                          label="Channel Name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`channelName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any channel name"
                            value={filters.channelName}
                            onChange={value => handleFilterChange('channelName', value)}
                            options={filterOptions.channelNames.map(name => ({ label: name, value: name }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Status"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`status-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="All Status"
                            value={filters.status}
                            onChange={value => handleFilterChange('status', value)}
                            options={filterOptions.status.map(status => ({ label: status, value: status }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Type"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`type-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="All"
                            value={filters.type}
                            onChange={value => handleFilterChange('type', value)}
                            options={filterOptions.types.map(type => ({ label: type, value: type }))}
                          />
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>

          {/* Channel Cards Section */}
          <div style={{ marginTop: '32px' }}>
            <Row gutter={[16, 16]} style={{ justifyContent: 'center' }}>
              {filteredChannels.map(c => (
                <Col key={c.id} xs={12} sm={6} md={3} lg={3} xl={3} style={{ marginBottom: '16px' }}>
                  <Card 
                    className={styles.rounded12}
                    style={{ 
                      cursor: 'pointer', 
                      transition: 'all 0.2s ease',
                      border: '2px solid transparent',
                      height: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#3498db';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(52,152,219,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'transparent';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                    }}
                    onClick={() => navigate(`/channel-programming/${c.id}`)}
                  >
                    <img 
                      src={getChannelArtwork(c.artwork)} 
                      alt={c.name}
                      style={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '50%', 
                        objectFit: 'cover',
                        marginBottom: 16 
                      }} 
                    />
                    <h3 style={{ 
                      color: '#2c3e50', 
                      marginBottom: 8, 
                      fontSize: 18 
                    }}>
                      {c.name}
                    </h3>
                    <p style={{ 
                      color: '#7f8c8d', 
                      fontSize: 12, 
                      textAlign: 'center' 
                    }}>
                      {c.description}
                    </p>
                    <div style={{
                      marginTop: 12,
                      padding: '4px 12px',
                      backgroundColor: c.status === 'Live' ? '#e8f5e8' : '#fff3cd',
                      color: c.status === 'Live' ? '#2d5a2d' : '#856404',
                      borderRadius: 12,
                      fontSize: 10,
                      fontWeight: '600'
                    }}>
                      {c.status}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Test;
